import { CodeRounded, Devices } from '@mui/icons-material'
import { Badge, BadgeTypeMap, IconButton } from '@mui/material'

/**
 * Define a development environment badge item.
 * @category General Layout
 */
export interface DevEnvironmentBadgeItem {
  /** The hostname of the development environment. */
  hostname: string
  /** The label of the development environment. */
  label: string
  /** The color of the development environment. */
  color: BadgeTypeMap['props']['color']
  /**  The icon of the development environment. */
  icon: JSX.Element
  /** Motivational message of the development environment. */
  message?: string
}

/**
 * Default list of development environments.
 */
const defaultDevEnvironments: DevEnvironmentBadgeItem[] = [
  {
    hostname: 'localhost',
    label: 'DEV',
    color: 'info',
    icon: <CodeRounded></CodeRounded>,
    message: 'Happy Coding!',
  },
  {
    hostname: 'beta.comstruct.com',
    label: 'BETA',
    color: 'warning',
    icon: <Devices></Devices>,
  },
]

/**
 * This interface is referencing the {@link EnvironmentBadge} component props.
 * @category General Layout
 */
export interface EnvironmentBadgeProps {
  /** A list of development environment badge items. */
  environments?: DevEnvironmentBadgeItem[]
}

/**
 * Build a badge for DEV and BETA Environments.
 * This badge is used to show the current environment in the UI.
 * Thus, developers do not get confused when switching between environments.
 * @param props
 * @category General Layout
 */
export default function EnvironmentBadge(
  props: EnvironmentBadgeProps
): JSX.Element | null {
  const devEnvironment = (props.environments ?? defaultDevEnvironments).find(
    ({ hostname }) => hostname === window.location.hostname
  )

  const isDevEnvironmentWithAlertMessage =
    devEnvironment &&
    devEnvironment.hostname === 'localhost' &&
    devEnvironment.message
  const onClickAction = isDevEnvironmentWithAlertMessage
    ? { onClick: () => alert(devEnvironment.message) }
    : {}

  return devEnvironment ? (
    <IconButton
      color={'inherit'}
      aria-label={devEnvironment.label}
      {...onClickAction}
    >
      <Badge
        color={devEnvironment.color}
        badgeContent={devEnvironment.label}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {devEnvironment.icon}
      </Badge>
    </IconButton>
  ) : null
}
